import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import QRCode from 'react-qr-code';
import { Smartphone, Share2, Link, Users, Clock, QrCode } from 'lucide-react';
import { Card } from '../components/ui/Card';
import { PageLayout } from '@/components/layout/PageLayout';
import {
  fetchMobileShareSessions,
  createMobileShareLink,
  fetchMobileShareContacts,
  fetchMobileShareStats,
  type MobileShareSession,
} from '../services/sync-share-service';

const MobileShare: React.FC = () => {
  const [showQR, setShowQR] = useState(false);
  const [sessions, setSessions] = useState<MobileShareSession[]>([]);
  const [contacts, setContacts] = useState<Array<{ name: string; avatar: string; last_shared: string }>>([]);
  const [stats, setStats] = useState({ activeShares: 0, totalTabs: 0, recipients: 0, thisWeek: 0 });
  const [shareUrl, setShareUrl] = useState('');
  const [loading, setLoading] = useState(true);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [sessionList, contactList, shareStats] = await Promise.all([
        fetchMobileShareSessions(),
        fetchMobileShareContacts(),
        fetchMobileShareStats(),
      ]);
      setSessions(sessionList);
      setContacts(contactList);
      setStats({
        activeShares: shareStats.active_shares ?? 0,
        totalTabs: shareStats.total_tabs_shared ?? 0,
        recipients: shareStats.recipients ?? 0,
        thisWeek: shareStats.this_week ?? 0,
      });
    } catch (err) {
      console.error('Failed to load share data:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleCreateShare = async () => {
    try {
      const link = await createMobileShareLink('session');
      setShareUrl(link.url);
      setShowQR(true);
      await loadData();
    } catch (err) {
      console.error('Failed to create share link:', err);
    }
  };

  const handleShareSession = async (session: MobileShareSession) => {
    if (session.url) {
      setShareUrl(session.url);
      setShowQR(true);
      return;
    }
    await handleCreateShare();
  };

  return (
    <PageLayout title="Mobile Share" description="Share tabs and sessions across your mobile devices" icon={Share2}>
      <div className="mb-8 flex justify-end gap-3">
        <button
          onClick={handleCreateShare}
          className="flex items-center space-x-2 px-6 py-3 bg-primary-500 text-white rounded-xl font-medium shadow-lg"
        >
          <Share2 className="h-5 w-5" />
          <span>New Share</span>
        </button>
        <button
          onClick={() => setShowQR(!showQR)}
          className="flex items-center space-x-2 px-6 py-3 bg-success-500 text-khiimori-text-primary rounded-xl font-medium shadow-lg"
        >
          <QrCode className="h-5 w-5" />
          <span>QR Share</span>
        </button>
      </div>

      {showQR && shareUrl && (
        <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="mb-8">
          <Card className="bg-gradient-glass border-success-400/30">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-khiimori-text-primary">QR Code Share</h3>
              <button onClick={() => setShowQR(false)} className="text-khiimori-text-muted hover:text-khiimori-text-secondary">✕</button>
            </div>
            <div className="flex flex-col items-center justify-center py-8 gap-4">
              <div className="p-4 bg-white rounded-lg shadow-lg">
                <QRCode value={shareUrl} size={192} />
              </div>
              <p className="text-sm text-khiimori-text-secondary break-all text-center max-w-md">{shareUrl}</p>
            </div>
            <p className="text-center text-sm text-khiimori-text-secondary">
              Scan to receive shared tabs on your mobile device
            </p>
          </Card>
        </motion.div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        {[
          { label: 'Active Shares', value: stats.activeShares, textClass: 'text-success-500' },
          { label: 'Total Tabs Shared', value: stats.totalTabs, textClass: 'text-success-400' },
          { label: 'Recipients', value: stats.recipients, textClass: 'text-info-500' },
          { label: 'This Week', value: stats.thisWeek, textClass: 'text-khiimori-accent-violet' },
        ].map((stat, index) => (
          <motion.div key={index} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.1 }}>
            <Card hover>
              <div className="text-center">
                <div className={`text-2xl font-bold ${stat.textClass}`}>
                  {stat.value}
                </div>
                <div className="text-sm text-khiimori-text-secondary">{stat.label}</div>
              </div>
            </Card>
          </motion.div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        <div>
          <h2 className="text-lg font-semibold text-khiimori-text-primary mb-4">Share Sessions</h2>
          {loading && <p className="text-khiimori-text-muted">Loading sessions...</p>}
          {!loading && sessions.length === 0 && (
            <Card>
              <p className="text-khiimori-text-secondary text-center py-6">
                No share sessions yet. Create a new share to generate a QR code or link.
              </p>
            </Card>
          )}
          <div className="space-y-4">
            {sessions.map((session, index) => (
              <motion.div key={session.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.05 }}>
                <Card hover>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4 flex-1">
                      <div className={`p-3 rounded-xl ${session.status === 'active' ? 'bg-success-500' : 'bg-khiimori-bg-tertiary'}`}>
                        <Share2 className="h-6 w-6 text-khiimori-text-primary" />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-bold text-khiimori-text-primary">{session.name}</h3>
                        <div className="flex items-center space-x-4 text-sm text-khiimori-text-secondary">
                          <span>{session.tab_count} tabs</span>
                          <span>•</span>
                          <span className="flex items-center"><Users className="h-4 w-4 mr-1" /> {session.shared_with?.length ?? 0}</span>
                          <span>•</span>
                          <span className="flex items-center"><Clock className="h-4 w-4 mr-1" /> {new Date(session.created_at).toLocaleDateString()}</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-3">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${session.status === 'active' ? 'bg-success-500/20 text-success-500' : 'bg-khiimori-bg-tertiary text-khiimori-text-secondary'}`}>
                        {session.status}
                      </span>
                      <button onClick={() => handleShareSession(session)} className="p-2 hover:bg-khiimori-bg-elevated rounded-lg">
                        <Share2 className="h-5 w-5 text-success-500" />
                      </button>
                    </div>
                  </div>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>

        <div>
          <h2 className="text-lg font-semibold text-khiimori-text-primary mb-4">Quick Share Contacts</h2>
          <Card>
            <div className="space-y-3">
              {contacts.map((contact, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-khiimori-bg-secondary rounded-xl hover:bg-khiimori-bg-elevated cursor-pointer transition-colors">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-success-500 rounded-full flex items-center justify-center text-khiimori-text-primary font-bold">
                      {contact.avatar}
                    </div>
                    <div>
                      <h4 className="font-medium text-khiimori-text-primary">{contact.name}</h4>
                      <span className="text-xs text-khiimori-text-muted">
                        Last shared: {new Date(contact.last_shared).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                  <button onClick={handleCreateShare} className="px-4 py-2 bg-success-500 text-khiimori-text-primary rounded-lg text-sm font-medium hover:bg-success-600">
                    Share
                  </button>
                </div>
              ))}
            </div>
          </Card>
        </div>
      </div>

      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }}>
        <Card className="bg-gradient-glass border-success-400/30 dark:border-success-400/20">
          <div className="flex items-center space-x-4">
            <div className="p-3 bg-success-500 rounded-xl">
              <Link className="h-6 w-6 text-khiimori-text-primary" />
            </div>
            <div>
              <h3 className="font-bold text-khiimori-text-primary">Instant Cross-Device Sharing</h3>
              <p className="text-sm text-khiimori-text-secondary">Share tabs to any device with a single tap or QR scan</p>
            </div>
          </div>
        </Card>
      </motion.div>
    </PageLayout>
  );
};

export default MobileShare;
